import { useMutation } from '@apollo/client/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  CHAT_FLOW_STEPS,
  CHAT_INPUT_MODES,
  setInputMode,
  useCurrentChat,
} from '@guuru/chat-web';
import ChatMessageBubble from '../../ChatMessageBubble';
import OptionsList from '../../OptionsList';
import SUBMIT_CHAT_BUTTON_MESSAGE from './mutations/SubmitChatButtonMessage';

interface ChatRenderButtonProps {
  chatId: string;
  message: {
    id: string;
    createdAt: number;
    text: string;
    metadata: {
      submitted: boolean;
      buttons: React.ComponentProps<typeof OptionsList>['options'];
    };
  };
  readOnly?: boolean;
  onSubmitChatText?: (_: {
    text: string;
    inputMode: string;
    chatId: string;
  }) => void;
}

const ChatRenderButton = function ({
  chatId,
  message,
  onSubmitChatText = () => {},
  readOnly = false,
}: ChatRenderButtonProps) {
  const { flowStep } = useCurrentChat();
  const [submitChatButtonMessage] = useMutation(SUBMIT_CHAT_BUTTON_MESSAGE);
  const [disabled, setDisabled] = useState(
    message.metadata.submitted || readOnly || flowStep !== CHAT_FLOW_STEPS.chat,
  );

  const messageId = useMemo(() => message.id.split('.')[0], [message.id]);

  useEffect(() => {
    if (!disabled) {
      setInputMode(CHAT_INPUT_MODES.none);
    }
    if (flowStep !== CHAT_FLOW_STEPS.chat) {
      setDisabled(true);
    }
  }, [disabled, flowStep]);

  const onClick = useCallback(
    async (button: { name: string }) => {
      setDisabled(true);
      await submitChatButtonMessage({ variables: { chatId, messageId } });
      onSubmitChatText({
        text: button.name,
        inputMode: CHAT_INPUT_MODES.chat,
        chatId,
      });
      setInputMode(CHAT_INPUT_MODES.chat);
    },
    [chatId, messageId, onSubmitChatText, submitChatButtonMessage],
  );

  return (
    <ChatMessageBubble fullWidth>
      <OptionsList
        message=""
        options={message.metadata.buttons}
        onSelection={onClick}
        readOnly={disabled}
      />
    </ChatMessageBubble>
  );
};

export default withTranslation()(ChatRenderButton);
